import { chakra, useMultiStyleConfig, ChakraProps } from "@chakra-ui/react";
import React, {
	BaseSyntheticEvent,
	ReactElement,
	useCallback,
	useMemo,
} from "react";
import {
	FieldValues,
	FormProvider,
	useFormContext,
	UseFormReturn,
} from "react-hook-form";
import {
	useGetAllProductCategoriesForPremiumCalcQuery,
	useGetAllProductsForFormsQuery,
} from "src/__generated__/client.codegen";
import { FormSelect } from "src/components/FormSelect/FormSelect";
import { FormSubmitButton } from "src/components/FormSubmitButton";
import { Hx } from "src/components/Heading/Heading";
import { InfoTooltip } from "src/components/InfoTooltip/InfoTooltip";
import { Loading } from "src/components/Loading/Loading";
import { useTranslatedString } from "src/i18n/hooks";
import { Checkmark, Submit } from "src/icons";
import { useSiteSettings, useTargetGroup } from "src/lib/hooks";
import { useErrorToast } from "src/lib/toasts";
import { z } from "src/lib/zod";
import { useSendEmail } from "src/queries/emil/email";
import { TypeOf } from "zod";

export const commonEmailPropsSchema = z.object({
	emailSubject: z.string(),
	productOrCategory: z.string().min(1).or(z.undefined()),
	categoryProduct: z.string().min(1).or(z.undefined()),
});

type ContactFormProps<ZodSchema extends FieldValues> = ChakraProps & {
	children: ReactElement | Array<ReactElement>;
	buttonText: string;
	isValid: boolean;
	type: "message" | "callback" | "demo";
	form: UseFormReturn<ZodSchema>;
};
export const ContactForm = <ZodSchema extends FieldValues>({
	buttonText,
	isValid,
	children,
	type,
	form,
	...rest
}: ContactFormProps<ZodSchema>) => {
	const styles = useMultiStyleConfig("ContactForm");
	const t = useTranslatedString();
	const {
		locale,
		brandConfig: { slug: brandSlug },
	} = useSiteSettings();

	const { data } = useGetAllProductsForFormsQuery({
		locale,
	});

	const products = useMemo(() => {
		return (
			data?.allProductPages.filter(
				(product) => product.showContactForm,
			) ?? []
		);
	}, [data]);

	const sendEmail = useSendEmail();
	const toast = useErrorToast();

	const targetGroup = useTargetGroup();

	const { handleSubmit } = form;

	const onSubmit = useCallback(
		(e: BaseSyntheticEvent) => {
			handleSubmit(
				({
					emailSubject,
					productOrCategory,
					categoryProduct,
					...payload
				}) => {
					const templateSlugAccessor = `${type}FormSlug` as const;

					let emailTemplateSlug;

					if (targetGroup) {
						emailTemplateSlug = targetGroup[templateSlugAccessor];

						if (!emailTemplateSlug) {
							toast(t("toast.formSubmitError"));

							return;
						}

						sendEmail.mutate({
							emailSubject: `[${brandSlug}] ${emailSubject}`,
							emailTemplateSlug,
							payload,
						});
					}

					const userSelectedProduct = products.find(
						(product) =>
							product.slug === productOrCategory ||
							product.slug === categoryProduct,
					);

					if (!userSelectedProduct) return;

					emailTemplateSlug =
						userSelectedProduct[templateSlugAccessor];

					if (!emailTemplateSlug) {
						// Something went wrong on our end (CMS content)
						toast(t("toast.formSubmitError"));

						return;
					}

					const productName = userSelectedProduct.name;
					const additionalInfo = `${productName}`;

					sendEmail.mutate({
						emailSubject: `[${brandSlug}] ${emailSubject}${additionalInfo}`,
						emailTemplateSlug,
						payload,
					});
				},
			)(e);
		},
		[
			handleSubmit,
			type,
			targetGroup,
			products,
			sendEmail,
			brandSlug,
			toast,
			t,
		],
	);

	// TODO: 🚧 Better error handling

	if (sendEmail.isSuccess) {
		return (
			<chakra.div __css={styles.formWrapper}>
				<chakra.div sx={styles.content}>
					<chakra.div sx={styles.formSent}>
						<Checkmark
							width={14}
							height={14}
							color="brand.highlight"
						/>
						<Hx size="h3">{t("sent")}</Hx>
					</chakra.div>
				</chakra.div>
			</chakra.div>
		);
	}

	return (
		<FormProvider {...form}>
			<chakra.div __css={styles.formWrapper} {...rest}>
				<chakra.form
					sx={styles.content}
					onSubmit={onSubmit}
					id={`form_${type}`}
				>
					{children}
					<chakra.div sx={styles.buttonWrapper}>
						<FormSubmitButton
							isValid={isValid}
							rightIcon={<Submit />}
							width="100%"
							isLoading={sendEmail.isPending}
							forceDisabled={sendEmail.isPending}
							id={`submit_contact_form_${type}`}
						>
							{buttonText}
						</FormSubmitButton>
					</chakra.div>
				</chakra.form>
			</chakra.div>
		</FormProvider>
	);
};

export const ContactFormTitle: React.FC<{
	children: ReactElement | Array<ReactElement>;
}> = ({ children }) => {
	const styles = useMultiStyleConfig("ContactForm");
	const t = useTranslatedString();

	return (
		<chakra.div sx={styles.formTitleWrapper}>
			<chakra.div sx={styles.formTitle}>{children}</chakra.div>
			<InfoTooltip text={t("contact.consent")} />
		</chakra.div>
	);
};

export const ContactFormProductSelect: React.FC = () => {
	const styles = useMultiStyleConfig("ContactForm");
	const t = useTranslatedString();
	const { locale } = useSiteSettings();
	const {
		formState: { errors },
		watch,
	} = useFormContext<TypeOf<typeof commonEmailPropsSchema>>();
	const { data: productData, isPending: isPendingProducts } =
		useGetAllProductsForFormsQuery({
			locale,
		});

	const { data: productCategoryData, isPending: isPendingProductCategories } =
		useGetAllProductCategoriesForPremiumCalcQuery({ locale });

	const targetGroup = useTargetGroup();

	if (targetGroup) {
		return null;
	}

	const isPending = isPendingProducts || isPendingProductCategories;

	const products =
		productData?.allProductPages.filter(
			(product) => product.showContactForm,
		) ?? [];
	const productCategories = productCategoryData?.allProductCategories ?? [];

	const singleProducts = products.filter(
		(product) => !product.productCategory,
	);

	const productsOrCategories = [...singleProducts, ...productCategories];

	const categoryProducts = products.filter(
		({ productCategory }) =>
			productCategory &&
			productCategory.slug === watch("productOrCategory"),
	);

	if (isPending) {
		<chakra.div __css={styles.formWrapper}>
			<chakra.div sx={styles.content}>
				<Loading />
			</chakra.div>
		</chakra.div>;
	}

	return (
		<>
			<FormSelect
				name="productOrCategory"
				label={t("contact.form.productOrCategory.label")}
				placeholder={t("contact.form.productOrCategory.placeholder")}
				isInvalid={Boolean(errors.productOrCategory)}
				sx={styles.formControl}
			>
				{productsOrCategories.map(({ slug, name }) => (
					<option key={slug} value={slug}>
						{name}
					</option>
				))}
			</FormSelect>
			{categoryProducts.length > 0 && (
				<FormSelect
					name="categoryProduct"
					label={t("contact.form.categoryProduct.label")}
					placeholder={t("contact.form.categoryProduct.placeholder")}
					sx={styles.formControl}
					isInvalid={Boolean(errors.categoryProduct)}
				>
					{categoryProducts.map(({ slug, name }) => {
						return (
							<option id={slug} key={slug} value={slug}>
								{name}
							</option>
						);
					})}
				</FormSelect>
			)}
		</>
	);
};

// 🔬 e2e: contact.spec.ts
